import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, User, LogOut, Users, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import HeaderBrandLogo from "../common/HeaderBrandLogo";
import { buildTenantPath } from "../../utils/tenantSlug";
import { useTenantSlug } from "../../context/TenantSlugContext";
import { useClinicProfile } from "../../context/ClinicProfileContext";
import { hasWhatsNewForRole } from "../../content/whatsNew";
import { openWhatsNew } from "../common/WhatsNewExperience";

const Header = () => {
  const { user, logout, isAuthenticated, requestClientSwitch, selectedClient, getDashboardPath } = useAuth(); // Added requestClientSwitch
  const { resolvedTenant } = useTenantSlug();
  const { effectiveTenantSlug } = useClinicProfile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const isTenantContext = Boolean(selectedClient || resolvedTenant);
  const dashboardRole = selectedClient?.userRole || user?.userGlobalRole;
  const whatsNewRole = dashboardRole || user?.userRole;
  const canViewWhatsNew = hasWhatsNewForRole(whatsNewRole);
  const tenantPath = (path: string) => buildTenantPath(path, effectiveTenantSlug);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const getDashboardLink = () => {
    if (!dashboardRole) return tenantPath("/");
    return getDashboardPath(dashboardRole, selectedClient?.clientSlug || effectiveTenantSlug);
  };

  const navLinks = [
    { name: "Início", path: tenantPath("/") },
    { name: "Sobre", path: tenantPath("/sobre") },
    { name: "Serviços", path: tenantPath("/servicos") },
    { name: "Notícias", path: tenantPath("/noticias") },
    { name: "Eventos", path: tenantPath("/eventos") },
    { name: "Cursos", path: tenantPath("/cursos") },
    { name: "Contato", path: tenantPath("/contato") },
    {
      name: isTenantContext ? "Agendar Consulta" : "Assinar Revahub",
      path: isTenantContext ? tenantPath("/agendar") : tenantPath("/cadastro"),
      highlight: true,
    },
  ];

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md py-0" : "bg-white py-0"}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-24 sm:h-28">
          <div className="flex-shrink-0">
            <HeaderBrandLogo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  link.highlight
                    ? "bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
                    : location.pathname === link.path
                    ? "text-primary-600"
                    : "text-neutral-800 hover:text-primary-600"
                }`}
              >
                {link.name}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-1 text-neutral-800 hover:text-primary-600"
                >
                  <span className="text-sm font-medium">{user?.userDisplayName}</span>
                  <ChevronDown size={16} />
                </button>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                    >
                      <Link
                        to={getDashboardLink()}
                        className="flex px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 w-full text-left"
                      >
                        <User size={16} className="mr-2" />
                        Minha Área
                      </Link>
                      {canViewWhatsNew && (
                        <button
                          type="button"
                          onClick={() => {
                            openWhatsNew();
                            setDropdownOpen(false);
                          }}
                          className="flex w-full items-center px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-100"
                        >
                          <Sparkles size={16} className="mr-2" />
                          Novidades
                        </button>
                      )}
                      <button
                        onClick={() => {
                          requestClientSwitch();
                          setDropdownOpen(false);
                        }}
                        className="flex px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 w-full text-left"
                      >
                        <Users size={16} className="mr-2" />
                        Alterar Tenant
                      </button>
                      <button
                        onClick={() => {
                          logout();
                          setDropdownOpen(false);
                        }}
                        className="flex px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 w-full text-left"
                      >
                        <LogOut size={16} className="mr-2" />
                        Sair
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to={tenantPath("/login")}
                className="text-sm font-medium text-neutral-800 hover:text-primary-600 transition-colors duration-200"
              >
                Entrar
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden z-50 p-2 rounded-md hover:bg-neutral-100"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, x: "100%" }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: "100%" }}
                transition={{ type: "tween", duration: 0.3 }}
                className="fixed inset-0 bg-white z-40 lg:hidden overflow-y-auto"
              >
                <div className="flex flex-col min-h-screen">
                  <div className="flex-1 px-4 pt-24 pb-6">
                    <nav className="space-y-3">
                      {navLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className={`block py-3 px-4 rounded-md text-base font-medium transition duration-200 ${
                            link.highlight
                              ? "bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
                              : location.pathname === link.path
                              ? "text-primary-600"
                              : "text-neutral-800 hover:text-neutral-600"
                          }`}
                          onClick={closeMenu}
                        >
                          {link.name}
                        </Link>
                      ))}

                      <div className="mt-4 pt-4 border-t border-neutral-200">
                        {isAuthenticated ? (
                          <div className="space-y-3">
                            <Link
                              to={getDashboardLink()}
                              className="flex items-center px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-100 rounded-md"
                              onClick={closeMenu}
                            >
                              <User size={20} className="mr-3" />
                              Minha Área
                            </Link>
                            {canViewWhatsNew && (
                              <button
                                type="button"
                                onClick={() => {
                                  openWhatsNew();
                                  closeMenu();
                                }}
                                className="flex w-full items-center rounded-md px-4 py-3 text-left text-base font-medium text-neutral-800 hover:bg-neutral-100"
                              >
                                <Sparkles size={20} className="mr-3" />
                                Novidades
                              </button>
                            )}
                             <button
                                onClick={() => {
                                  requestClientSwitch();
                                  closeMenu();
                                }}
                                className="flex items-center w-full px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-100 rounded-md"
                              >
                                <Users size={20} className="mr-3" />
                                Alterar Tenant
                              </button>
                            <button
                              onClick={() => {
                                logout();
                                closeMenu();
                              }}
                              className="flex items-center w-full px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-100 rounded-md"
                            >
                              <LogOut size={20} className="mr-3" />
                              Sair
                            </button>
                          </div>
                        ) : (
                          <Link
                            to={tenantPath("/login")}
                            className="flex items-center px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-100 rounded-md"
                            onClick={closeMenu}
                          >
                            <User size={20} className="mr-3" />
                            Entrar
                          </Link>
                        )}
                      </div>
                    </nav>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;
